import { defineStore } from 'pinia';
import type { RouteParamsRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { ref } from 'vue';
import { watchOnce } from '@vueuse/core';

import type { Landing } from '@libs/trip-planner-interfaces';

import { TripPlannerPage } from '../models/navigation';

export const useRouterStore = defineStore('RouterStore', () => {
  const router = useRouter();
  const route = useRoute();

  // State
  const isNavigating = ref(false);
  const landingRoute = ref<TripPlannerPage>();
  const isInitialLoad = ref<boolean>(true);
  const landing = ref<Landing | undefined>(undefined);
  const previousTripPlannerPage = ref<TripPlannerPage | undefined>(undefined);

  // Getters - functions
  function getCurrentTripPlannerView(): TripPlannerPage | undefined {
    return Object.values(TripPlannerPage).find(
      (tripPlannerPage) => tripPlannerPage === route.name
    );
  }

  function getTripOverviewUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/overview${queryParams}`;
  }

  function getHotelSelectionUrl(ingredientIndex: number): string {
    const queryParams = window.location.search;
    return `/travel/plan/hotel-selection/${ingredientIndex}${queryParams}`;
  }

  function getHotelDetailsUrlInHotelSelection(
    ingredientIndex: number,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    return `/travel/plan/hotel-selection/${ingredientIndex}/hotel-details/${hotelCode}${queryParams}`;
  }

  function getFlightListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/flights${queryParams}`;
  }

  function getFerryListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/ferry${queryParams}`;
  }

  function getActivitySelectionUrl(ingredientIndex: number): string {
    const queryParams = window.location.search;
    return `/travel/plan/activity-selection/${ingredientIndex}${queryParams}`;
  }

  // Actions
  function setLanding(param: Landing): void {
    landing.value = param;
  }

  function setLandingRoute(): void {
    landingRoute.value = getCurrentTripPlannerView();
  }

  async function navigateToTripOverview(): Promise<void> {
    await navigate({ name: TripPlannerPage.TripOverview });
  }

  async function navigateToCarTrip(): Promise<void> {
    await navigate({ name: TripPlannerPage.CarTrip });
  }

  async function navigateToFlightsList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FlightList });
  }

  async function navigateToFerryList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FerryList });
  }

  async function navigateToHotelSelection(recipeIndex: number): Promise<void> {
    // NOTE: recipeState cannot be injected in this store file to find index directly here, due to circular dependency:
    // recipeState -> editingIngredientStore -> routerStore
    await navigate({
      name: TripPlannerPage.HotelSelection,
      params: { recipeIndex },
    });
  }

  async function navigateToActivitySelection(
    recipeIndex: number
  ): Promise<void> {
    // NOTE: recipeState cannot be injected in this store file to find index directly here, due to circular dependency:
    // recipeState -> editingIngredientStore -> routerStore
    await navigate({
      name: TripPlannerPage.ActivitySelection,
      params: { recipeIndex },
    });
  }

  async function navigateToHotelDetails(
    recipeIndex: number,
    hotelCode: string
  ): Promise<void> {
    await navigate({
      name: TripPlannerPage.HotelDetails,
      params: { recipeIndex, hotelCode },
    });
  }

  async function navigateToTripOverviewPrint(): Promise<void> {
    await navigate({
      name: TripPlannerPage.TripOverviewPrint,
    });
  }

  async function navigateToHotelDetailsInHotelSelection(
    recipeIndex: number,
    hotelCode: string
  ): Promise<void> {
    await navigate({
      name: TripPlannerPage.HotelDetailsInHotelSelection,
      params: { recipeIndex, hotelCode },
    });
  }

  async function navigate({
    name,
    params,
  }: {
    name: TripPlannerPage;
    params?: RouteParamsRaw;
  }): Promise<void> {
    await afterNavigation();
    await router.push({
      name,
      params,
      query: route.query,
    });
  }

  async function afterNavigation(): Promise<void> {
    return new Promise((resolve) => {
      if (!isNavigating.value) {
        resolve();
      } else {
        watchOnce(isNavigating, async () => resolve());
      }
    });
  }

  return {
    // State
    isInitialLoad,
    isNavigating,
    landing,
    landingRoute,
    previousTripPlannerPage,

    // Getters - functions
    getCurrentTripPlannerView,
    getTripOverviewUrl,
    getHotelSelectionUrl,
    getHotelDetailsUrlInHotelSelection,
    getFlightListUrl,
    getFerryListUrl,
    getActivitySelectionUrl,

    // Actions
    navigateToActivitySelection,
    navigateToFerryList,
    navigateToFlightsList,
    navigateToHotelDetails,
    navigateToHotelSelection,
    navigateToTripOverview,
    navigateToCarTrip,
    navigateToTripOverviewPrint,
    navigateToHotelDetailsInHotelSelection,
    setLanding,
    setLandingRoute,
  };
});
